import moment from 'moment'
import attendanceService from '@/services/attendance-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ATTENDANCE_PERMISSIONS } from '@/utils/attendance-permissions'
import { isAuthFailure, handleSessionExpiry } from '@/utils/session-expiry'

export default {
  methods: {
    handleAuthFailureIfNeeded(resOrErr) {
      if (!isAuthFailure(resOrErr)) return false
      handleSessionExpiry(this)
      return true
    },
    // ============================================
    // LEAVE APPLICATION
    // ============================================
    async handleApplyLeaveV2() {
      const isHalfDay = this.leaveForm.is_half_day === true

      if (!this.leaveForm.from_date || !this.leaveForm.to_date || !this.leaveForm.leave_type || !this.leaveForm.comment) {
        this.$toast({ component: ToastificationContent, props: { title: 'Please fill all required fields including comment', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
        return
      }

      if (isHalfDay && !this.leaveForm.half_day_type) {
        this.$toast({ component: ToastificationContent, props: { title: 'Please select 1st Half or 2nd Half', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
        return
      }

      if (isHalfDay && this.leaveForm.from_date !== this.leaveForm.to_date) {
        this.leaveForm.to_date = this.leaveForm.from_date
      }

      const targetEmpId = (this.leaveForm.selected_employee && this.leaveForm.selected_employee !== this.emp_id)
        ? this.leaveForm.selected_employee
        : (this.viewMode === 'team' && this.selectedTeamMember && this.selectedTeamMember !== this.emp_id)
          ? this.selectedTeamMember
          : this.emp_id

      const fromMoment = moment(this.leaveForm.from_date)
      const toMoment = moment(this.leaveForm.to_date)

      if (!fromMoment.isValid() || !toMoment.isValid()) {
        this.$toast({ component: ToastificationContent, props: { title: 'Invalid date format', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
        return
      }

      let leaveTypeId = null
      const selectedLeaveType = this.leaveForm.leave_type
      const selectedLeaveTypeUpper = selectedLeaveType ? selectedLeaveType.toUpperCase() : ''

      if (selectedLeaveType && this.leaveTypesFromDB && this.leaveTypesFromDB.length > 0) {
        const foundType = this.leaveTypesFromDB.find(lt =>
          lt.code === selectedLeaveType ||
          lt.display_name === selectedLeaveType ||
          (lt.code && lt.code.toUpperCase() === selectedLeaveTypeUpper),
        )
        if (foundType) leaveTypeId = foundType.id
      }

      if (!leaveTypeId && this.filteredLeaveTypeOptions) {
        const foundOption = this.filteredLeaveTypeOptions.find(opt =>
          opt.value === selectedLeaveType || opt.text === selectedLeaveType,
        )
        if (foundOption && foundOption.id) leaveTypeId = foundOption.id
      }

      const PROBATION_NEVER_OVERRIDE = ['WFH', 'WORK FROM HOME', 'CO', 'CO_REQUEST',
        'COMPENSATORY', 'COMPENSATORY OFF', 'COMPENSATORY LEAVE', 'SL', 'SICK', 'SICK LEAVE',
        'MATERNITY', 'MATERNITY LEAVE', 'PATERNITY', 'PATERNITY LEAVE', 'UNPAID', 'UNPAID LEAVE']
      const PROBATION_OVERRIDE_ALLOWED = ['PL', 'CL', 'PRIVILEGE LEAVE', 'CASUAL LEAVE']
      const leaveTypeUpper = (this.leaveForm.leave_type || '').toUpperCase()

      let finalLeaveType = this.leaveForm.leave_type
      const isWFHType = leaveTypeUpper === 'WFH' || leaveTypeUpper === 'WORK FROM HOME'
      if (isWFHType) finalLeaveType = 'Work From Home'

      const isProtectedType = PROBATION_NEVER_OVERRIDE.some(t => leaveTypeUpper === t || leaveTypeUpper.includes(t))

      const preserveLeaveType = isProtectedType ||
        (this.isOnProbation && !PROBATION_OVERRIDE_ALLOWED.some(t => leaveTypeUpper === t || leaveTypeUpper.includes(t)))

      if (isProtectedType && finalLeaveType && finalLeaveType.toLowerCase().includes('probation')) {
        console.error('[ProbationGuard] leave_type is Probation Leave for a protected type. Aborting submission.')
        this.leaveForm.leave_type = null
        this.$toast({ component: ToastificationContent, props: { title: 'Please re-select your leave type and try again.', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 4000 })
        return
      }

      const leaveData = {
        emp_id: targetEmpId,
        from_date: fromMoment.format('YYYY-MM-DD'),
        to_date: toMoment.format('YYYY-MM-DD'),
        leave_type: finalLeaveType,
        leave_type_id: leaveTypeId,
        comment: this.leaveForm.comment,
        is_half_day: isHalfDay,
        half_day_type: isHalfDay ? this.leaveForm.half_day_type : null,
        applied_by: targetEmpId !== this.emp_id ? this.emp_id : null,
        preserve_leave_type: preserveLeaveType || undefined,
      }

      if (this.leaveForm.id) leaveData.id = this.leaveForm.id

      try {
        const res = this.leaveForm.id
          ? await attendanceService.updateLeave(leaveData)
          : await attendanceService.applyLeave(leaveData)

        if (this.handleAuthFailureIfNeeded(res)) return

        if (res && res.status === 'OK') {
          let successMessage = this.leaveForm.id ? 'Leave updated successfully!' : 'Leave applied successfully!'

          if (res.data && res.data.switched_from) {
            successMessage = `${successMessage} (${res.data.switch_reason})`
          }

          if (res.data && res.data.warnings && res.data.warnings.length > 0) {
            res.data.warnings.forEach(warning => {
              this.$toast({ component: ToastificationContent, props: { title: warning.message || warning, icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 4000 })
            })
          }

          this.$toast({ component: ToastificationContent, props: { title: successMessage, icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
          this.$refs.leaveApplicationModal.hide()
          await this.loadData()
        } else if (res && res.status === 'VALIDATION_ERROR') {
          const errors = res.errors || []
          const suggestions = res.suggestions || []

          if (errors.length > 0) {
            this.$toast({ component: ToastificationContent, props: { title: errors[0].message || errors[0], icon: 'AlertCircleIcon', variant: 'danger' } }, { timeout: 5000 })
          }

          const unpaidSuggestion = suggestions.find(s => s.action === 'unpaid_leave')
          if (unpaidSuggestion) {
            const result = await this.$swal({
              title: 'Unpaid Leave Warning',
              html: `You don't have enough leave balance. <strong>${unpaidSuggestion.unpaid_days}</strong> day(s) will be <strong>UN-PAID</strong>.<br><br>Do you want to proceed?`,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonText: 'Yes, Apply as Unpaid',
              cancelButtonText: 'Cancel',
              confirmButtonColor: '#667eea',
              cancelButtonColor: '#6c757d',
            })

            if (result.isConfirmed) {
              leaveData.accept_unpaid = true
              const retryRes = await (leaveData.id ? attendanceService.updateLeave(leaveData) : attendanceService.applyLeave(leaveData))

              if (this.handleAuthFailureIfNeeded(retryRes)) return

              if (retryRes && retryRes.status === 'OK') {
                this.$toast({ component: ToastificationContent, props: { title: 'Leave applied successfully (includes unpaid days)', icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
                this.$refs.leaveApplicationModal.hide()
                await this.loadData()
              } else {
                this.$toast({ component: ToastificationContent, props: { title: retryRes?.message || 'Failed to apply leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
              }
            }
          }
        } else {
          this.$toast({ component: ToastificationContent, props: { title: res?.message || 'Failed to apply leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
        }
      } catch (err) {
        console.error('Error applying leave:', err)

        if (this.handleAuthFailureIfNeeded(err)) return

        if (err.response && err.response.data) {
          const errorData = err.response.data

          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorData.errors.forEach((error, index) => {
              setTimeout(() => {
                this.$toast({ component: ToastificationContent, props: { title: error.message || error, icon: 'AlertCircleIcon', variant: 'danger', text: error.details || '' } }, { timeout: 5000 })
              }, index * 100)
            })
            return
          }

          if (errorData.message) {
            this.$toast({ component: ToastificationContent, props: { title: errorData.message, icon: 'AlertCircleIcon', variant: 'danger' } }, { timeout: 4000 })
            return
          }
        }

        this.$toast({ component: ToastificationContent, props: { title: 'An error occurred while applying leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
      }
    },
    async handleApplyLeave() {
      return this.handleApplyLeaveV2()
    },
    async openApplyLeaveModal() {
      this.resetLeaveForm()
      await this.loadLeaveTypeOptions()

      // If in team view and a team member is selected, pre-fill the employee dropdown
      // so the leave is applied to that member by default (mirrors openWFHModal behavior)
      if (this.viewMode === 'team' && this.selectedTeamMember && this.selectedTeamMember !== this.emp_id) {
        this.leaveForm.selected_employee = this.selectedTeamMember
        await this.handleLeaveEmployeeChange()
      }

      this.showApplyLeaveModal = true
      this.$refs.leaveApplicationModal.show()
    },
    resetLeaveForm() {
      this.showApplyLeaveModal = false
      this.leaveForm = {
        from_date: null,
        to_date: null,
        leave_type: null,
        is_half_day: false,
        half_day_type: null,
        comment: '',
        apply_to: this.isOnProbation ? 'hr' : 'manager',
        selected_employee: null,
      }
      this.selectedEmployeeForLeaveConfig = null
      this.selectedEmployeeForLeaveStats = null
      this.selectedEmployeeForLeaveLeaves = []
      this.leaveDateRange = null
      this.calendarMonth = moment().month()
      this.calendarYear = moment().year()
      this.datePickerKey = Date.now()
      this.apiLeaveTypeOptions = []
    },
    clearDateSelection() {
      this.leaveForm.from_date = null
      this.leaveForm.to_date = null
      this.leaveDateRange = null
      this.$forceUpdate()
    },
    getLeaveDaysCount() {
      if (!this.leaveForm.from_date) return ''

      const isHalfDay = this.leaveForm.is_half_day === true
      if (isHalfDay) return '0.5'

      if (this.leaveForm.to_date && this.leaveForm.from_date === this.leaveForm.to_date) {
        const dateStr = this.leaveForm.from_date
        const dateMoment = moment(dateStr)
        const dayOfWeek = dateMoment.day()
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
        const isHoliday = this.holidays.some(h => {
          const holidayDate = h.holiday_date || h.date
          return holidayDate === dateStr
        })
        return (isWeekend || isHoliday) ? '0' : '1'
      }

      if (this.leaveForm.to_date) {
        return this.calculateDays(this.leaveForm.from_date, this.leaveForm.to_date).toString()
      }

      return '1'
    },
    handleLeaveTypeChange() {
      const supportsHalfDay = this.selectedLeaveTypeSupportsHalfDay

      if (!supportsHalfDay) {
        this.leaveForm.is_half_day = false
        this.leaveForm.half_day_type = null
      } else {
        if (!this.leaveForm.is_half_day) {
          this.leaveForm.half_day_type = null
        }
      }

      if (this.leaveForm.is_half_day && this.leaveForm.from_date) {
        this.leaveForm.to_date = this.leaveForm.from_date
        this.$forceUpdate()
      }

      this.unpaidLeaveAcknowledged = false

      if (this.leaveForm.leave_type && this.leaveForm.from_date && this.leaveForm.to_date) {
        this.checkLeaveBalance()
      }
    },
    handleLeaveDurationChange() {
      if (!this.leaveForm.is_half_day) {
        this.leaveForm.half_day_type = null
      } else if (!this.leaveForm.half_day_type) {
        this.leaveForm.half_day_type = '1st Half Day'
      }

      if (this.leaveForm.is_half_day && this.leaveForm.from_date) {
        this.leaveForm.to_date = this.leaveForm.from_date
        this.$forceUpdate()
      }

      if (this.leaveForm.from_date && this.leaveForm.to_date) {
        this.checkLeaveBalance()
      }
    },
    navigateCalendarMonth(direction) {
      if (direction === -1) {
        if (this.calendarMonth === 0) {
          this.calendarMonth = 11
          this.calendarYear--
        } else {
          this.calendarMonth--
        }
      } else if (direction === 1) {
        if (this.calendarMonth === 11) {
          this.calendarMonth = 0
          this.calendarYear++
        } else {
          this.calendarMonth++
        }
      }
    },
    selectCalendarDate(day) {
      if (!day.isCurrentMonth) {
        const clickedDate = moment(day.dateStr)
        this.calendarMonth = clickedDate.month()
        this.calendarYear = clickedDate.year()
        this.$nextTick(() => {
          this.selectCalendarDate({ ...day, isCurrentMonth: true })
        })
        return
      }

      const isHalfDay = this.leaveForm.is_half_day === true

      if (isHalfDay) {
        this.leaveForm.from_date = day.dateStr
        this.leaveForm.to_date = day.dateStr
        const selectedDate = moment(day.dateStr)
        this.calendarMonth = selectedDate.month()
        this.calendarYear = selectedDate.year()
      } else {
        if (!this.leaveForm.from_date || (this.leaveForm.from_date && this.leaveForm.to_date)) {
          this.leaveForm.from_date = day.dateStr
          this.leaveForm.to_date = null
          const selectedDate = moment(day.dateStr)
          this.calendarMonth = selectedDate.month()
          this.calendarYear = selectedDate.year()
        } else if (this.leaveForm.from_date && !this.leaveForm.to_date) {
          const fromDate = moment(this.leaveForm.from_date)
          const clickedDate = moment(day.dateStr)

          if (clickedDate.isBefore(fromDate, 'day')) {
            this.leaveForm.to_date = this.leaveForm.from_date
            this.leaveForm.from_date = day.dateStr
            this.calendarMonth = clickedDate.month()
            this.calendarYear = clickedDate.year()
          } else {
            this.leaveForm.to_date = day.dateStr
            const fromDateMoment = moment(this.leaveForm.from_date)
            if (fromDateMoment.month() !== clickedDate.month() || fromDateMoment.year() !== clickedDate.year()) {
              this.calendarMonth = fromDateMoment.month()
              this.calendarYear = fromDateMoment.year()
            }
          }
        }
      }
    },
    formatDateForDisplay(date) {
      if (!date) return ''
      return moment(date).format('DD MMM YYYY')
    },
    getCalendarDayClasses(day) {
      return {
        'other-month': !day.isCurrentMonth,
        'selected-start': day.isSelectedStart,
        'selected-end': day.isSelectedEnd,
        'selected-range': day.isInRange,
        'today': day.isToday,
        'disabled': day.isDisabled,
        'past-date': day.isPast,
        'already-applied': day.isAlreadyApplied,
        'holiday-disabled': day.isHoliday,
      }
    },
    saveAsDraft() {
      this.$toast({ component: ToastificationContent, props: { title: 'Draft saved successfully!', icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
    },
    async handleFromDateChange(selectedDates, dateStr) {
      let fromDate = null
      if (selectedDates && selectedDates.length > 0) {
        fromDate = moment(selectedDates[0]).format('YYYY-MM-DD')
      } else if (dateStr) {
        fromDate = moment(dateStr, 'DD-MM-YYYY').format('YYYY-MM-DD')
      }

      if (fromDate) {
        this.leaveForm.from_date = fromDate
        if (this.leaveForm.to_date && moment(this.leaveForm.to_date).isBefore(fromDate, 'day')) {
          this.leaveForm.to_date = null
        }
        const selectedDate = moment(fromDate)
        this.calendarMonth = selectedDate.month()
        this.calendarYear = selectedDate.year()
        await this.loadLeaveTypeOptions(this.leaveForm.from_date)
      } else {
        this.leaveForm.from_date = null
        if (this.leaveForm.to_date) {
          this.leaveForm.to_date = null
        }
      }
      this.$forceUpdate()
    },
    async handleToDateChange(selectedDates, dateStr) {
      let toDate = null
      if (selectedDates && selectedDates.length > 0) {
        toDate = moment(selectedDates[0]).format('YYYY-MM-DD')
      } else if (dateStr) {
        toDate = moment(dateStr, 'DD-MM-YYYY').format('YYYY-MM-DD')
      }

      if (toDate) {
        if (this.leaveForm.from_date && moment(toDate).isBefore(this.leaveForm.from_date, 'day')) {
          this.$toast({ component: ToastificationContent, props: { title: 'To date cannot be before From date', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
          this.leaveForm.to_date = null
          return
        }
        this.leaveForm.to_date = toDate
        const selectedDate = moment(toDate)
        this.calendarMonth = selectedDate.month()
        this.calendarYear = selectedDate.year()
      } else {
        this.leaveForm.to_date = null
      }
      this.$forceUpdate()
    },
    async handleLeaveDateRangeChange(selectedDates, dateStr) {
      if (selectedDates && selectedDates.length === 2) {
        const fromDate = moment(selectedDates[0]).format('YYYY-MM-DD')
        const toDate = moment(selectedDates[1]).format('YYYY-MM-DD')

        this.leaveForm.from_date = fromDate
        this.leaveForm.to_date = toDate
        this.leaveDateRange = [
          moment(selectedDates[0]).format('DD-MM-YYYY'),
          moment(selectedDates[1]).format('DD-MM-YYYY'),
        ]

        const startDate = moment(selectedDates[0])
        this.calendarMonth = startDate.month()
        this.calendarYear = startDate.year()

        await this.loadLeaveTypeOptions(this.leaveForm.from_date)

        if (this.leaveForm.leave_type) {
          this.checkLeaveBalance()
        }
      } else if (selectedDates && selectedDates.length === 1) {
        const fromDate = moment(selectedDates[0]).format('YYYY-MM-DD')

        this.leaveForm.from_date = fromDate
        this.leaveForm.to_date = null
        this.leaveDateRange = [moment(selectedDates[0]).format('DD-MM-YYYY')]

        await this.loadLeaveTypeOptions(this.leaveForm.from_date)
        const selectedDate = moment(selectedDates[0])
        this.calendarMonth = selectedDate.month()
        this.calendarYear = selectedDate.year()
      } else {
        this.leaveForm.from_date = null
        this.leaveForm.to_date = null
        this.leaveDateRange = null
        this.unpaidLeaveAcknowledged = false
      }
      this.$forceUpdate()
    },
    checkLeaveBalance() {
      if (this.leaveForm.leave_type === 'Compensatory' || this.leaveForm.leave_type === 'Compensatory Leave') return
      if (!this.leaveForm.leave_type || !this.leaveForm.from_date || !this.leaveForm.to_date) return

      let breakdown = {}
      let availableBalance = 0
      let assignedBalance = 0
      let leaveTypeName = ''

      if (this.dashboardData && this.dashboardData.leave_balance_breakdown && this.dashboardData.leave_balance_breakdown.length > 0) {
        const breakdownArray = this.dashboardData.leave_balance_breakdown
        const leaveType = this.leaveForm.leave_type

        let breakdownItem = null
        if (leaveType === 'Casual' || leaveType === 'Casual Leave') {
          breakdownItem = breakdownArray.find(item => item.type === 'casual_leave')
          leaveTypeName = 'Casual Leave'
        } else if (leaveType === 'Privilege Leave' || leaveType === 'Privilege') {
          breakdownItem = breakdownArray.find(item => item.type === 'privilege_leave')
          leaveTypeName = 'Privilege Leave'
        } else if (leaveType === 'Sick' || leaveType === 'Sick Leave' || leaveType === 'Medical' || leaveType === 'Medical Leave') {
          breakdownItem = breakdownArray.find(item => item.type === 'sick_leave')
          leaveTypeName = 'Sick Leave'
        } else if (leaveType === 'Paternity' || leaveType === 'Paternity Leave') {
          breakdownItem = breakdownArray.find(item => item.type === 'paternity_leave')
          leaveTypeName = 'Paternity Leave'
        } else if (leaveType === 'Maternity' || leaveType === 'Maternity Leave') {
          breakdownItem = breakdownArray.find(item => item.type === 'maternity_leave')
          leaveTypeName = 'Maternity Leave'
        } else {
          return
        }

        if (breakdownItem) {
          assignedBalance = breakdownItem.assigned || 0
          availableBalance = breakdownItem.available || 0
        } else {
          return
        }
      } else {
        breakdown = (this.employeeConfig && this.employeeConfig.leaveBalanceBreakdown)
          ? this.employeeConfig.leaveBalanceBreakdown
          : (this.leaveStats && Object.keys(this.leaveStats).length > 0) ? this.leaveStats : {}

        if (!breakdown || Object.keys(breakdown).length === 0) return

        const usage = this.leaveUsageByType
        const leaveType = this.leaveForm.leave_type

        if (leaveType === 'Casual' || leaveType === 'Casual Leave') {
          leaveTypeName = 'Casual Leave'
          assignedBalance = breakdown.casual_leave || 0
          availableBalance = Math.max(0, assignedBalance - (usage['casual_leave'] || 0))
        } else if (leaveType === 'Privilege Leave' || leaveType === 'Privilege') {
          leaveTypeName = 'Privilege Leave'
          const cfTotal = breakdown.carry_forward || 0
          const currentMonth = moment().month() + 1
          const plTaken = usage['privilege_leave'] || 0
          assignedBalance = cfTotal + currentMonth
          availableBalance = Math.max(0, assignedBalance - plTaken)
        } else if (leaveType === 'Sick' || leaveType === 'Sick Leave' || leaveType === 'Medical' || leaveType === 'Medical Leave') {
          leaveTypeName = 'Sick Leave'
          const sickAssigned = (breakdown.sick_leave || 0) + (breakdown.medical_leave || 0)
          assignedBalance = sickAssigned
          availableBalance = Math.max(0, sickAssigned - (usage['sick_leave'] || 0))
        } else if (leaveType === 'Paternity' || leaveType === 'Paternity Leave') {
          leaveTypeName = 'Paternity Leave'
          assignedBalance = breakdown.paternity_leave || 0
          availableBalance = Math.max(0, assignedBalance - (usage['paternity_leave'] || 0))
        } else if (leaveType === 'Maternity' || leaveType === 'Maternity Leave') {
          leaveTypeName = 'Maternity Leave'
          assignedBalance = breakdown.maternity_leave || 0
          availableBalance = Math.max(0, assignedBalance - (usage['maternity_leave'] || 0))
        } else {
          return
        }
      }

      if (assignedBalance === 0 && availableBalance === 0) return

      const fromDate = moment(this.leaveForm.from_date).startOf('day')
      const toDate = moment(this.leaveForm.to_date).startOf('day')
      const isHalfDay = this.leaveForm.is_half_day === true

      let requestedDays = 0
      if (isHalfDay) {
        requestedDays = 0.5
      } else {
        let totalWorkingDays = 0
        let currentDate = fromDate.clone()
        while (currentDate.isSameOrBefore(toDate, 'day')) {
          const dayOfWeek = currentDate.day()
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
          const isHoliday = this.holidays.some(h => {
            const holidayDate = moment(h.holiday_date || h.date).startOf('day')
            return currentDate.isSame(holidayDate, 'day')
          })
          if (!isWeekend && !isHoliday) totalWorkingDays++
          currentDate.add(1, 'day')
        }
        requestedDays = totalWorkingDays
      }

      if (requestedDays > availableBalance) {
        const exceededDays = requestedDays - availableBalance
        const exceededDaysText = exceededDays === 0.5 ? '0.5 day' : exceededDays === 1 ? '1 day' : `${this.roundToHalf(exceededDays)} days`
        const availableText = availableBalance === 0 ? '0' : availableBalance === 0.5 ? '0.5' : this.roundToHalf(availableBalance).toString()

        this.$swal({
          title: 'Unpaid Leave Warning',
          html: `You have <strong>${availableText}</strong> ${leaveTypeName.toLowerCase()} available, but you're trying to apply for <strong>${this.roundToHalf(requestedDays)}</strong> days.<br><br>The exceeded <strong>${exceededDaysText}</strong> will be <strong>UN-PAID</strong>.<br><br>Are you sure you want to apply?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, Apply',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#667eea',
          cancelButtonColor: '#6c757d',
        }).then(result => {
          if (result.isConfirmed) {
            this.unpaidLeaveAcknowledged = true
            this.$toast({ component: ToastificationContent, props: { title: 'Unpaid leave acknowledged', text: `${exceededDaysText} will be marked as unpaid`, icon: 'InfoIcon', variant: 'info' } }, { timeout: 3000 })
          } else {
            this.unpaidLeaveAcknowledged = false
          }
        })
      } else {
        this.unpaidLeaveAcknowledged = false
      }
    },
    async handleLeaveEmployeeChange() {
      if (this.leaveForm.selected_employee) {
        try {
          const empId = this.leaveForm.selected_employee

          if (this.viewMode === 'team' && this.teamDashboardData && this.teamDashboardData.team_leave_balances) {
            const perMember = this.teamDashboardData.team_leave_balances.per_member || []
            const memberData = perMember.find(m => m.emp_id === empId)
            if (memberData) {
              this.selectedEmployeeForLeaveConfig = { emp_id: empId, leave_balance_breakdown: memberData.breakdown || [] }
              try {
                const leavesRes = await attendanceService.getMyLeaves(empId)
                this.selectedEmployeeForLeaveLeaves = (leavesRes && leavesRes.status === 'OK' && leavesRes.data) ? leavesRes.data || [] : []
              } catch (leavesErr) {
                console.error('Error loading selected employee leaves:', leavesErr)
                this.selectedEmployeeForLeaveLeaves = []
              }
              const leavesTaken = typeof this.calculateLeavesAvailedFromLeaves === 'function'
                ? this.calculateLeavesAvailedFromLeaves(this.selectedEmployeeForLeaveLeaves)
                : Math.max(0, (memberData.total_used || 0) - (memberData.wfh_count || 0))
              this.selectedEmployeeForLeaveStats = { leaveBalance: memberData.total_available || 0, leavesTaken }
              return
            }
          }

          const [configRes, statsRes, leavesRes] = await Promise.all([
            attendanceService.getEmployeeConfig(empId),
            attendanceService.getLeaveStats(empId),
            attendanceService.getMyLeaves(empId),
          ])

          this.selectedEmployeeForLeaveConfig = (configRes && configRes.status === 'OK' && configRes.data) ? configRes.data : null
          this.selectedEmployeeForLeaveLeaves = (leavesRes && leavesRes.status === 'OK' && leavesRes.data) ? leavesRes.data || [] : []
          const statsData = (statsRes && statsRes.status === 'OK' && statsRes.data) ? statsRes.data : null
          if (statsData) {
            const leavesTaken = typeof this.calculateLeavesAvailedFromLeaves === 'function'
              ? this.calculateLeavesAvailedFromLeaves(this.selectedEmployeeForLeaveLeaves)
              : Math.max(0, (statsData.leavesTaken || 0) - (statsData.WFHCount || 0))
            this.selectedEmployeeForLeaveStats = { ...statsData, leavesTaken }
          } else {
            this.selectedEmployeeForLeaveStats = null
          }
        } catch (err) {
          console.error('Error loading selected employee data:', err)
          this.selectedEmployeeForLeaveConfig = null
          this.selectedEmployeeForLeaveStats = null
          this.selectedEmployeeForLeaveLeaves = []
        }
      } else {
        this.selectedEmployeeForLeaveConfig = null
        this.selectedEmployeeForLeaveStats = null
        this.selectedEmployeeForLeaveLeaves = []
      }
    },

    // ============================================
    // LEAVE APPROVAL / REJECTION
    // ============================================
    getApprovalStatusForLeave(leave) {
      if (!leave || !this.canApproveLeave(leave)) return null
      if (this.canApproveLeaveAllStages) {
        return { status: 'Approved', isFinalApproval: true, successMessage: 'Leave approved successfully!' }
      }
      if (this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_MANAGER) && this.isManagerOfEmployee(leave.emp_id) && this.isLeavePendingManagerApproval(leave)) {
        if (this.isApplicantOperations(leave)) {
          return { status: 'Manager Approved - Pending Operation Head', isFinalApproval: false, successMessage: 'Leave approved by Manager. Sent to Operation Head.' }
        }
        return { status: 'HR Pending', isFinalApproval: false, successMessage: 'Leave approved by Manager. Sent to HR for final approval.' }
      }
      if (this.isApplicantOperations(leave) && this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_OPERATION_HEAD) && this.isLeavePendingOperationHeadApproval(leave)) {
        return { status: 'Operation Head Approved - Pending HR', isFinalApproval: false, successMessage: 'Leave approved by Operation Head. Sent to HR for final approval.' }
      }
      if (this.$can('approve', ATTENDANCE_PERMISSIONS.APPROVE_LEAVE_HR) && this.isLeavePendingHRApproval(leave)) {
        return { status: 'Approved', isFinalApproval: true, successMessage: 'Leave approved successfully!' }
      }
      return null
    },
    async approveLeave(leave) {
      this.selectedLeaveForApproval = leave
      this.approvalIsCompensatory = leave.leave_type === 'Compensatory' || leave.leave_type === 'Compensatory Leave'
      this.approvalComment = ''
      this.$bvModal.show('modal-approve-leave')
    },
    async handleApproveLeaveConfirm() {
      if (!this.selectedLeaveForApproval) return

      if (!this.approvalComment || !this.approvalComment.trim()) {
        this.$toast({ component: ToastificationContent, props: { title: 'Comment Required', text: 'Please provide a comment for approval', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
        return
      }

      const leave = this.selectedLeaveForApproval
      const isCompensatoryLeave = this.approvalIsCompensatory
      const approverComment = this.approvalComment || ''

      try {
        const approvalResult = this.getApprovalStatusForLeave(leave)
        if (!approvalResult) {
          this.$toast({ component: ToastificationContent, props: { title: 'Cannot approve', text: 'You do not have permission to approve this leave.', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
          return
        }

        const { status: statusToSet, isFinalApproval, successMessage } = approvalResult
        const approverType = (this.userRole || 'EMPLOYEE').toUpperCase().replace(/\s+/g, '-')

        let comment = approverComment.trim()
        if (isFinalApproval && (leave.manager_comment || leave.manager_remarks)) {
          const managerComment = leave.manager_comment || leave.manager_remarks || ''
          if (managerComment && comment) comment = `${managerComment} | ${comment}`
          else if (managerComment) comment = managerComment
        }

        const approvalData = {
          leave_id: leave.id,
          approver_id: this.emp_id,
          approver_type: approverType,
          comment,
          status: statusToSet,
          approved_by: this.emp_id,
          approvedAt: new Date().toISOString(),
          is_compensatory: isCompensatoryLeave,
          approver_comment: approverComment.trim(),
        }
        if (isFinalApproval && this.isApplicantOnProbation(leave)) {
          approvalData.is_paid = this.approvalIsPaid
        }

        const res = await attendanceService.approveLeave(approvalData)

        if (res && res.status === 'OK') {
          const isCOBalanceRequest = leave.leave_type === 'CO_REQUEST' ||
                                     leave.leave_type === 'CO Request' ||
                                     (leave.comment && leave.comment.includes('CO Balance Request'))

          if (isFinalApproval && statusToSet === 'Approved' && !isCOBalanceRequest) {
            try {
              await this.createAttendanceEntriesForLeave(leave)
            } catch (attendanceErr) {
              console.error('Error creating attendance entries:', attendanceErr)
              this.$toast({ component: ToastificationContent, props: { title: 'Leave approved but attendance entries creation failed', text: 'Please create attendance entries manually.', icon: 'AlertTriangleIcon', variant: 'warning' } }, { timeout: 5000 })
            }
          }

          this.$toast({ component: ToastificationContent, props: { title: successMessage, icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
          this.$bvModal.hide('modal-approve-leave')
          this.selectedLeaveForApproval = null
          this.approvalComment = ''
          this.approvalStatusToSet = 'Approved'
          this.approvalIsCompensatory = false
          this.approvalIsPaid = true
          await this.loadData()
        } else {
          this.$toast({ component: ToastificationContent, props: { title: res?.message || 'Failed to approve leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
        }
      } catch (err) {
        console.error('Error approving leave:', err)
        this.$toast({ component: ToastificationContent, props: { title: 'An error occurred while approving leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
      }
    },
    async rejectLeave(leave) {
      this.selectedLeaveForRejection = leave
      this.rejectionComment = ''
      this.$bvModal.show('modal-reject-leave')
    },
    async handleRejectLeaveConfirm() {
      if (!this.selectedLeaveForRejection) return

      if (!this.rejectionComment || !this.rejectionComment.trim()) {
        this.$toast({ component: ToastificationContent, props: { title: 'Comment Required', text: 'Please provide a comment for rejection', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
        return
      }

      const leave = this.selectedLeaveForRejection
      const rejectionComment = this.rejectionComment || ''

      try {
        const canRejectFinal = this.canApproveLeaveAllStages
        const approverType = (this.userRole || 'EMPLOYEE').toUpperCase().replace(/\s+/g, '-')

        let comment = rejectionComment.trim()
        if (canRejectFinal && leave.manager_comment) {
          const managerComment = leave.manager_comment || leave.manager_remarks || ''
          if (managerComment && rejectionComment.trim()) {
            comment = `${managerComment} | ${rejectionComment.trim()}`
          } else if (managerComment) {
            comment = managerComment
          }
        }

        const status = canRejectFinal ? 'HR Reject' : 'Rejected By Manager'

        const rejectionData = {
          leave_id: leave.id,
          approver_id: this.emp_id,
          approver_type: approverType,
          comment,
          status,
          rejected_by: this.emp_id,
          rejectedAt: new Date().toISOString(),
          approver_comment: rejectionComment.trim(),
        }

        const res = await attendanceService.rejectLeave(rejectionData)

        if (res && res.status === 'OK') {
          this.$toast({ component: ToastificationContent, props: { title: 'Leave rejected successfully!', icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
          this.$bvModal.hide('modal-reject-leave')
          this.selectedLeaveForRejection = null
          this.rejectionComment = ''
          await this.loadData()
        } else {
          this.$toast({ component: ToastificationContent, props: { title: res?.message || 'Failed to reject leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
        }
      } catch (err) {
        console.error('Error rejecting leave:', err)
        this.$toast({ component: ToastificationContent, props: { title: 'An error occurred while rejecting leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
      }
    },
    async createAttendanceEntriesForLeave(leave) {
      if (!leave.from_date || !leave.to_date || !leave.emp_id) {
        console.warn('Cannot create attendance entries: missing leave data')
        return
      }

      const fromDate = moment(leave.from_date).startOf('day')
      const toDate = moment(leave.to_date).startOf('day')
      let currentDate = fromDate.clone()
      const attendancePromises = []

      while (currentDate.isSameOrBefore(toDate, 'day')) {
        const dateStr = currentDate.format('YYYY-MM-DD')
        const dayOfWeek = currentDate.day()
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

        if (!isWeekend) {
          const isHoliday = this.holidays.some(h => {
            const holidayDate = moment(h.holiday_date || h.date).startOf('day')
            return currentDate.isSame(holidayDate, 'day')
          })

          if (!isHoliday) {
            let attendanceStatus = 'leave'
            let leaveCount = 1

            if (leave.leave_type === '1st Half Day' || leave.leave_type === '2nd Half Day') {
              leaveCount = 0.5
              attendanceStatus = 'leave'
            } else if (leave.leave_type === 'Present') {
              attendanceStatus = 'present'
              leaveCount = 0
            }

            const attendanceData = {
              emp_id: leave.emp_id,
              date: dateStr,
              attendance: attendanceStatus,
              leave_type: leave.leave_type,
              count: 1,
              leave_count: leaveCount,
              remarks: leave.comment || '',
              source_type: 'REGULARIZATION',
              status: 'approved',
              regularization_status: 'approved',
              regularized_by: leave.emp_id,
            }

            attendancePromises.push(attendanceService.addPost(attendanceData))
          }
        }

        currentDate.add(1, 'day')
      }

      if (attendancePromises.length > 0) {
        await Promise.allSettled(attendancePromises)
      }
    },
    async cancelLeave(leave) {
      this.$bvModal.msgBoxConfirm(
        'Are you sure you want to cancel this leave request?',
        {
          title: 'Cancel Leave Request',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes, Cancel',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        },
      ).then(async value => {
        if (value) {
          try {
            const res = await attendanceService.cancelLeave(leave.id, leave.emp_id || this.emp_id)
            if (res && res.status === 'OK') {
              this.$toast({ component: ToastificationContent, props: { title: 'Leave request cancelled successfully!', icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
              await this.loadData()
            } else {
              this.$toast({ component: ToastificationContent, props: { title: res?.message || 'Failed to cancel leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
            }
          } catch (err) {
            console.error('Error cancelling leave:', err)
            this.$toast({ component: ToastificationContent, props: { title: 'An error occurred while cancelling leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
          }
        }
      }).catch(err => {
        console.error('Error in cancel confirmation:', err)
      })
    },
    openLeaveDetailModal(leave) {
      this.selectedLeaveForDetail = leave
      this.leaveDetailComment = ''
      if (this.$refs['modal-leave-detail']) {
        this.$refs['modal-leave-detail'].show()
      }
    },
    editLeaveFromDetail(leave) {
      if (this.$refs['modal-leave-detail']) {
        this.$refs['modal-leave-detail'].hide()
      }
      this.editLeave(leave)
    },
    cancelLeaveFromDetail(leave) {
      if (this.$refs['modal-leave-detail']) {
        this.$refs['modal-leave-detail'].hide()
      }
      this.cancelLeave(leave)
    },
    async approveLeaveFromDetail(leave) {
      if (!this.leaveDetailComment || !this.leaveDetailComment.trim()) {
        this.$toast({ component: ToastificationContent, props: { title: 'Comment Required', text: 'Please provide a comment for approval', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
        return
      }

      const approvalResult = this.getApprovalStatusForLeave(leave)
      if (!approvalResult) {
        this.$toast({ component: ToastificationContent, props: { title: 'Cannot approve', text: 'You do not have permission to approve this leave.', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
        return
      }

      try {
        const isCompensatory = leave.leave_type === 'Compensatory' || leave.leave_type === 'Compensatory Leave' || leave.leave_type === 'CO'
        const approverType = (this.userRole || 'EMPLOYEE').toUpperCase().replace(/\s+/g, '-')
        let comment = this.leaveDetailComment.trim()
        if (approvalResult.isFinalApproval && (leave.manager_comment || leave.manager_remarks)) {
          const managerComment = leave.manager_comment || leave.manager_remarks || ''
          if (managerComment && comment) comment = `${managerComment} | ${comment}`
          else if (managerComment) comment = managerComment
        }

        const approvalData = {
          leave_id: leave.id,
          approver_id: this.emp_id,
          approver_type: approverType,
          comment,
          status: approvalResult.status,
          is_compensatory: isCompensatory,
          approved_by: this.emp_id,
          approver_comment: this.leaveDetailComment.trim(),
        }
        if (approvalResult.isFinalApproval && this.isApplicantOnProbation(leave)) {
          approvalData.is_paid = this.leaveDetailIsPaid
        }

        const res = await attendanceService.approveLeave(approvalData)

        if (res && res.status === 'OK') {
          if (approvalResult.isFinalApproval && approvalResult.status === 'Approved') {
            const isCOBalanceRequest = leave.leave_type === 'CO_REQUEST' || leave.leave_type === 'CO Request' || (leave.comment && leave.comment.includes('CO Balance Request'))
            if (!isCOBalanceRequest) {
              try {
                await this.createAttendanceEntriesForLeave(leave)
              } catch (attendanceErr) {
                console.error('Error creating attendance entries:', attendanceErr)
              }
            }
          }
          this.$toast({ component: ToastificationContent, props: { title: approvalResult.successMessage, text: res.message || '', icon: 'CheckCircleIcon', variant: 'success' } }, { timeout: 3000 })
          if (this.$refs['modal-leave-detail']) {
            this.$refs['modal-leave-detail'].hide()
          }
          await this.loadData()
        } else {
          this.$toast({ component: ToastificationContent, props: { title: 'Approval Failed', text: res?.message || 'Failed to approve leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
        }
      } catch (err) {
        console.error('Error approving leave:', err)
        this.$toast({ component: ToastificationContent, props: { title: 'Error', text: err.response?.data?.message || 'An error occurred while approving leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
      }
    },
    async rejectLeaveFromDetail(leave) {
      if (!this.leaveDetailComment || !this.leaveDetailComment.trim()) {
        this.$toast({ component: ToastificationContent, props: { title: 'Comment Required', text: 'Please provide a comment for rejection', icon: 'AlertCircleIcon', variant: 'warning' } }, { timeout: 3000 })
        return
      }

      try {
        const rejectionData = {
          leave_id: leave.id,
          approver_id: this.emp_id,
          approver_type: this.userRole === 'HR' || this.userRole === 'HR-DIRECTOR' ? 'hr' : 'manager',
          comment: this.leaveDetailComment,
          status: 'Rejected',
          rejected_by: this.emp_id,
        }

        const res = await attendanceService.rejectLeave(rejectionData)

        if (res && res.status === 'OK') {
          this.$toast({ component: ToastificationContent, props: { title: 'Leave Rejected', text: res.message || 'Leave request has been rejected', icon: 'XCircleIcon', variant: 'success' } }, { timeout: 3000 })
          if (this.$refs['modal-leave-detail']) {
            this.$refs['modal-leave-detail'].hide()
          }
          await this.loadData()
        } else {
          this.$toast({ component: ToastificationContent, props: { title: 'Rejection Failed', text: res?.message || 'Failed to reject leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
        }
      } catch (err) {
        console.error('Error rejecting leave:', err)
        this.$toast({ component: ToastificationContent, props: { title: 'Error', text: err.response?.data?.message || 'An error occurred while rejecting leave', icon: 'XCircleIcon', variant: 'danger' } }, { timeout: 3000 })
      }
    },
    showMoreUpcomingLeaves() {
      this.displayedUpcomingLeavesLimit += 5
      if (this.displayedUpcomingLeavesLimit > this.upcomingLeaves.length) {
        this.displayedUpcomingLeavesLimit = this.upcomingLeaves.length
      }
    },
    showLessUpcomingLeaves() {
      this.displayedUpcomingLeavesLimit = 5
    },
    editLeave(leave) {
      const leaveType = (leave.leave_type || '').toLowerCase()
      if (leaveType === 'compensatory' || leaveType.includes('compensatory')) {
        let workDate = null
        if (leave.comment || leave.reason) {
          const commentText = leave.comment || leave.reason || ''
          const workDateMatch = commentText.match(/Work Date:\s*(\d{2}\s+\w+\s+\d{4})/)
          if (workDateMatch) {
            workDate = moment(workDateMatch[1], 'DD MMM YYYY').format('YYYY-MM-DD')
          }
        }

        let actualComment = leave.comment || leave.reason || ''
        const workDatePrefix = actualComment.match(/Work Date:.*?\.\s*/)
        if (workDatePrefix) {
          actualComment = actualComment.replace(workDatePrefix[0], '').trim()
        }

        this.compOffForm = {
          id: leave.id,
          work_date: workDate,
          date: leave.from_date,
          comment: actualComment,
          selected_employee: null,
        }
        this.$refs['modal-apply-compoff'].show()
      } else if (leaveType.includes('work from home') || leaveType === 'wfh') {
        this.wfhForm = {
          id: leave.id,
          date: leave.from_date,
          comment: leave.comment || leave.reason || '',
          partial_wfh: leave.partial_wfh === true,
          is_half_day: leave.is_half_day === true,
          half_day_type: leave.half_day_type || '1st Half Day',
          selected_employee: null,
        }
        this.$nextTick(() => {
          const wfhModal = this.$refs.wfhModal
          if (wfhModal && typeof wfhModal.show === 'function') {
            wfhModal.show()
          } else {
            this.$root.$emit('bv::show::modal', 'modal-apply-wfh')
          }
        })
      } else {
        this.leaveForm = {
          id: leave.id,
          leave_type: leave.leave_type,
          from_date: leave.from_date,
          to_date: leave.to_date,
          is_half_day: leave.is_half_day || false,
          half_day_type: leave.half_day_type || null,
          comment: leave.comment || leave.reason || '',
          reason: leave.reason || '',
          apply_to: leave.apply_to || '',
          approval_manager: leave.approval_manager || '',
          selected_employee: null,
        }
        this.leaveDateRange = [leave.from_date, leave.to_date]
        if (leave.from_date) {
          this.calendarMonth = moment(leave.from_date).month()
          this.calendarYear = moment(leave.from_date).year()
        }
        this.$nextTick(() => {
          this.$refs.leaveApplicationModal.show()
        })
      }
    },
    handleLeavesTakenClick(leaveTypeLabel) {
      const leaveType = leaveTypeLabel

      const isShowingAllTeam = (this.viewMode === 'team' || this.viewMode === 'org') &&
        !this.selectedTeamMember &&
        !this.selectedTeamWiseEmployee &&
        ((this.canApplyOnBehalfAll && this.selectedAccountManager && !this.selectedTeamWiseEmployee) ||
         (this.canApplyOnBehalfAll && !this.selectedAccountManager))

      let leavesToFilter
      if (isShowingAllTeam) {
        leavesToFilter = this.allTeamLeaves
      } else if (this.viewMode === 'team') {
        const selectedEmpId = this.selectedTeamMember || this.selectedTeamWiseEmployee
        leavesToFilter = this.allTeamLeaves.filter(leave => leave && leave.emp_id === selectedEmpId)
      } else {
        leavesToFilter = this.myLeaves.filter(leave => leave && leave.emp_id === (this.currentViewEmpId || this.emp_id))
      }

      const approvedLeaves = leavesToFilter.filter(leave => {
        const status = (leave.status || '').toLowerCase()
        return status === 'approved' || status === 'manager approved - pending hr'
      })

      const leaveTypeLower = leaveType.toLowerCase()
      const filtered = approvedLeaves.filter(leave => {
        const leaveTypeStr = (leave.leave_type || '').toLowerCase()

        if (leaveTypeLower.includes('privilege') || leaveTypeLower === 'pl') {
          const isHalfDay = leaveTypeStr.includes('half day') ||
                           leave.leave_type === '1st Half Day' ||
                           leave.leave_type === '2nd Half Day' ||
                           leave.half_day === true
          return leaveTypeStr.includes('privilege') || leaveTypeStr.includes('pto') || leaveTypeStr === 'pl' || isHalfDay
        } else if (leaveTypeLower.includes('casual') || leaveTypeLower === 'cl') {
          return leaveTypeStr.includes('casual') || leaveTypeStr === 'cl'
        } else if (leaveTypeLower.includes('sick') || leaveTypeLower === 'sl') {
          return leaveTypeStr.includes('sick') || leaveTypeStr.includes('medical') || leaveTypeStr === 'sl'
        } else if (leaveTypeLower.includes('paternity')) {
          return leaveTypeStr.includes('paternity')
        } else if (leaveTypeLower.includes('maternity')) {
          return leaveTypeStr.includes('maternity')
        } else if (leaveTypeLower.includes('compensatory') || leaveTypeLower === 'co' || leaveTypeLower === 'comp off') {
          return leaveTypeStr.includes('compensatory') || leaveTypeStr === 'co' || leaveTypeStr.includes('comp')
        } else if (leaveTypeLower.includes('work from home') || leaveTypeLower === 'wfh') {
          return leaveTypeStr.includes('work from home') || leaveTypeStr.includes('wfh')
        }
        return false
      })

      const enrichedFiltered = filtered.map(leave => {
        const empId = leave.emp_id || leave.empId
        const employeeName = this.getUserName(empId)
        return { ...leave, employee_name: employeeName }
      })

      this.selectedLeaveTypeForList = leaveTypeLabel
      this.filteredLeavesForList = enrichedFiltered
      this.$refs['modal-leaves-taken-list'].show()
    },
  },
}
